const cities = [
  { name: "Jupiter", zips: "33458, 33468, 33469, 33477, 33478" },
  { name: "Tequesta", zips: "33469" },
  { name: "Juno Beach", zips: "33408" },
  { name: "Palm Beach Gardens", zips: "33410, 33418" },
  { name: "North Palm Beach", zips: "33403, 33408" },
  { name: "Hobe Sound", zips: "33455" },
  { name: "Stuart", zips: "34994, 34995, 34996, 34997" },
  { name: "Jupiter Inlet Colony", zips: "33469" },
];

const benefits = [
  {
    icon: "ri-computer-line",
    title: "Start 100% Online",
    desc: "No office visit required to begin. Complete your order from home or work in minutes.",
  },
  {
    icon: "ri-truck-line",
    title: "Ship Your Documents",
    desc: "Use overnight shipping to send your documents directly to us — we handle the rest.",
  },
  {
    icon: "ri-shield-check-line",
    title: "Expert Document Review",
    desc: "Every application is reviewed by our team before submission to reduce errors and delays.",
  },
  {
    icon: "ri-calendar-check-line",
    title: "Reserved Agency Access",
    desc: "We hold reserved submission slots at the regional passport agency — no public appointment needed.",
  },
];

export default function JupiterLocalAreaSection() {
  return (
    <section id="service-area" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Service Area</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Serving Jupiter &amp; Northern Palm Beach County
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Whether you are in Jupiter, Tequesta, Juno Beach, or anywhere in northern Palm Beach County, Fast Passport Center makes expedited passport processing simple — no drive to Miami, no agency lines, no guessing.
          </p>
        </div>

        {/* Full-width editorial intro */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 md:p-8 mb-12">
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            Jupiter sits at the northern edge of Palm Beach County, where the Intracoastal Waterway meets the Atlantic Ocean and the Loxahatchee River winds through one of Florida's last remaining wild estuaries. Unlike the high-density developments of Boca Raton or the urban energy of West Palm Beach, Jupiter has maintained a distinctive coastal character — part fishing village, part affluent enclave, part outdoor paradise. The Jupiter Inlet Lighthouse, standing since 1860 on a natural limestone ridge, remains the visual anchor of the town and one of the most photographed landmarks in South Florida.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            The town's identity is deeply tied to the water. The Loxahatchee River — Florida's first federally designated Wild and Scenic River — draws kayakers, paddleboarders, and nature enthusiasts who explore its cypress-lined shores and spot manatees, otters, and wading birds. Offshore, the Gulf Stream runs close to the coast, creating some of the best sport fishing in the continental United States. Jupiter's fishing fleet — including the famous charter boats that operate out of the Jupiter Inlet — targets sailfish, mahi-mahi, wahoo, and tuna in waters that attract anglers from across the country and around the world. The town's marinas are filled with sportfishing yachts, and the docks at places like Square Grouper and Guanabanas serve as social hubs where captains, crews, and clients gather after a day on the water.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            Roger Dean Chevrolet Stadium, located just off Donald Ross Road, is the spring training home of both the St. Louis Cardinals and Miami Marlins. Every February and March, baseball fans from Jupiter and the surrounding communities fill the seats to watch major leaguers prepare for the upcoming season. The stadium complex also hosts minor league games throughout the summer, making it a year-round destination for sports enthusiasts. For many Jupiter residents, spring training is not just entertainment — it is a signal that winter is ending and travel season is beginning. The same crowds that cheer for the Cardinals in February are the ones booking fishing charters to the Bahamas, golf trips to Scotland, and family vacations to Europe in April and May.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            Jupiter's population reflects a unique mix of old Florida and new money. Longtime residents — fishing families, marine industry workers, and small business owners — live alongside affluent newcomers drawn by the town's natural beauty, top-rated schools, and relative seclusion compared to the denser communities to the south. Abacoa, a master-planned community built around the stadium, has become a model of New Urbanism in South Florida with its walkable town center, mixed-use development, and community events. Meanwhile, communities like Admirals Cove, Jupiter Island (technically its own town but culturally contiguous), and the Bear's Club offer waterfront estates and golf course homes that rank among the most expensive in Florida.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            What unites all of these residents is travel. The fishing community travels — to Costa Rica for billfish tournaments, to the Bahamas for multi-day charters, to Mexico for deep-water excursions. The affluent homeowners travel — to Europe for cultural tours, to the Caribbean for winter escapes, to Aspen and Vail for ski season. The baseball fans travel — to St. Louis to watch the Cardinals at Busch Stadium, to Atlanta for away games, to Cooperstown for the Hall of Fame. Young professionals in the growing tech and medical sectors travel for conferences, client meetings, and continuing education. Families travel for spring break, summer vacations, and holiday reunions.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            Palm Beach International Airport (PBI) is approximately 20 miles south on I-95 — about a 25-minute drive during normal traffic, though rush hour on I-95 can stretch that significantly. PBI is the preferred airport for most Jupiter residents because of its manageable size, shorter security lines, and direct international service. Fort Lauderdale-Hollywood International (FLL) is about 60 miles south and serves as a backup for international routes not available from PBI. For private aviation, the Palm Beach County Park Airport (Lantana) and North County General Aviation Airport in Palm Beach Gardens serve the general aviation community.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed">
            Fast Passport Center has processed expedited passports for Jupiter travelers across every demographic and travel scenario. We understand that a fishing captain with a confirmed charter to the Abacos in three days has the same urgency as a business executive flying to London for a board meeting. Our courier-based expedited service means you start online from your Abacoa townhome, your Jupiter Inlet Colony waterfront estate, or your Tequesta bungalow — and your passport arrives within days, not months.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left — FPC Office + How It Works */}
          <div>
            {/* FPC Office Card */}
            <div className="bg-[#1A365D] rounded-lg p-7 mb-8 text-white">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-[#F6C344] flex-shrink-0">
                  <i className="ri-building-2-line text-[#1A365D] text-xl"></i>
                </div>
                <div>
                  <p className="font-bold text-base">Fast Passport Center — Delray Beach</p>
                  <p className="text-white/60 text-xs mt-0.5">401 W Atlantic Ave #351, Delray Beach, FL 33444</p>
                </div>
              </div>
              <p className="text-white/70 text-sm mb-1">
                <i className="ri-map-pin-line text-[#F6C344] mr-1.5"></i>
                Nearest office to Jupiter — approximately 25 miles south on I-95
              </p>
              <p className="text-white/50 text-xs mb-5 ml-5">Courier pickup available throughout northern Palm Beach County and Martin County</p>
              <div className="border-t border-white/10 pt-5">
                <p className="text-white/80 text-sm leading-relaxed mb-4">
                  After signing up online, choose what works best for you: <strong className="text-white">courier pickup</strong> from your home or office in most cases, <strong className="text-white">ship with our prepaid label</strong> from any UPS/FedEx location in Jupiter or Palm Beach Gardens, or <strong className="text-white">schedule a drop-off</strong> at our Delray Beach office. Most clients ship overnight — no visit required.
                </p>
                <a
                  href="https://fastpassportcenter.com/checkout/"
                  className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-5 py-2.5 rounded-md transition-all whitespace-nowrap"
                >
                  Start My Passport <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>

            {/* How FPC Helps */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {benefits.map((b, i) => (
                <div key={i} className="bg-white rounded-lg p-5 border border-gray-200">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#F6C344]/15 mb-3">
                    <i className={`${b.icon} text-[#1A365D] text-base`}></i>
                  </div>
                  <p className="font-bold text-[#1A365D] text-sm mb-1">{b.title}</p>
                  <p className="text-gray-500 text-xs leading-relaxed">{b.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right — Cities We Serve + Trust strip */}
          <div>
            <div className="bg-white rounded-lg p-7 mb-6 border border-gray-200">
              <h3 className="font-bold text-[#1A365D] text-sm uppercase tracking-wide mb-5">
                <i className="ri-map-pin-2-line text-[#F6C344] mr-2"></i>
                Cities &amp; Communities We Serve
              </h3>
              <div className="space-y-1.5">
                {cities.map((city, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between py-2.5 px-3 bg-[#F7F8FA] rounded-md"
                  >
                    <div className="flex items-center gap-2.5">
                      <i className="ri-checkbox-circle-fill text-[#F6C344] text-sm"></i>
                      <span className="text-[#1A365D] font-medium text-sm">{city.name}</span>
                    </div>
                    {city.zips && (
                      <span className="text-gray-400 text-xs font-mono">{city.zips}</span>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-gray-500 text-xs mt-4 leading-relaxed">
                Anywhere in northern Palm Beach County or Martin County — start online, ship overnight, and we handle the rest. No need to drive to Miami.
              </p>
            </div>

            {/* Internal links */}
            <div className="bg-white rounded-lg p-5 mb-6 border border-gray-200">
              <p className="text-gray-400 text-xs font-semibold uppercase tracking-widest mb-3">Nearby Cities We Serve</p>
              <div className="flex flex-wrap gap-2">
                {[
                  { name: "Melbourne", href: "/melbourne" },
                  { name: "Stuart", href: "/stuart" },
                  { name: "Palm Beach Gardens", href: "/palm-beach-gardens" },
                  { name: "West Palm Beach", href: "/west-palm-beach" },
                  { name: "Palm Beach", href: "/palm-beach" },
                  { name: "Boca Raton", href: "/boca-raton" },
                  { name: "Delray Beach", href: "/delray-beach" },
                ].map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-[#F7F8FA] border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5 whitespace-nowrap"
                  >
                    {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
                  </a>
                ))}
              </div>
            </div>

            {/* Trust credentials strip */}
            <div className="bg-[#1A365D] rounded-lg p-5">
              <p className="text-white/50 text-xs uppercase tracking-widest font-semibold mb-4">Why Travelers Choose Fast Passport Center</p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: "ri-government-line", value: "Registered Courier", label: "U.S. Department of State" },
                  { icon: "ri-award-line", value: "A+ Rating", label: "Better Business Bureau" },
                  { icon: "ri-calendar-check-line", value: "20+ Years", label: "Serving Travelers Nationwide" },
                  { icon: "ri-customer-service-2-line", value: "Real Humans", label: "Live Support — Not a Bot" },
                ].map((item, i) => (
                  <a
                    key={i}
                    href="https://fastpassportcenter.com"
                    className="flex items-center gap-2.5 bg-white/5 hover:bg-white/10 rounded-md px-3 py-2.5 transition-colors cursor-pointer"
                  >
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
                      <i className={`${item.icon} text-sm text-[#F6C344]`}></i>
                    </div>
                    <div className="text-left">
                      <span className="text-white text-xs font-bold block leading-tight">{item.value}</span>
                      <span className="text-white/50 text-xs leading-tight block">{item.label}</span>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}